import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProgress } from "@/hooks/useProgress";
import { Progress } from "@/components/ui/progress";
import { Star, Flame, Award, Play, ArrowRight, Sparkles } from "lucide-react";

const categoryMeta: Record<string, { label: string; path: string; emoji: string; total: number }> = {
  alphabet: { label: "ABC & Numbers", path: "/alphabet", emoji: "🔤", total: 36 },
  english: { label: "English Learning", path: "/english-learning", emoji: "📖", total: 30 },
  math: { label: "Math Skills", path: "/math", emoji: "➗", total: 25 },
  spellings: { label: "Spellings", path: "/spellings", emoji: "🔡", total: 20 },
  sentences: { label: "Magic Words", path: "/magic-words", emoji: "💬", total: 20 },
  history: { label: "History", path: "/history", emoji: "🏛️", total: 15 },
  world: { label: "World Explorer", path: "/world-explorer", emoji: "🌍", total: 20 },
  helpers: { label: "Community Helpers", path: "/community-helpers", emoji: "👮", total: 12 },
  stories: { label: "Stories", path: "/stories", emoji: "📚", total: 20 },
};

const computeStreak = (dates: string[]): number => {
  if (!dates.length) return 0;
  const set = new Set(dates.map((d) => d.split("T")[0]));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 60; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().split("T")[0];
    if (set.has(key)) streak++;
    else if (i > 0) break;
  }
  return streak;
};

type Badge = { id: string; label: string; icon: string; threshold: number; metric: "stars" | "completed" | "streak" };

const allBadges: Badge[] = [
  { id: "first", label: "First Steps", icon: "👣", threshold: 1, metric: "completed" },
  { id: "rising", label: "Rising Star", icon: "⭐", threshold: 10, metric: "stars" },
  { id: "streak3", label: "3-Day Streak", icon: "🔥", threshold: 3, metric: "streak" },
  { id: "collector", label: "Star Collector", icon: "🌟", threshold: 25, metric: "stars" },
  { id: "scholar", label: "Mini Scholar", icon: "🎓", threshold: 10, metric: "completed" },
  { id: "master", label: "Star Master", icon: "🏆", threshold: 50, metric: "stars" },
];

const LearningJourney = () => {
  const { user } = useAuth();
  const { rows, totalStars, completedCount, last, loading } = useProgress();

  const streak = useMemo(
    () => computeStreak(rows.map((r) => r.updated_at || "").filter(Boolean)),
    [rows]
  );

  const { current, percent } = useMemo(() => {
    const lastCat = last?.category as keyof typeof categoryMeta | undefined;
    const cur = lastCat && categoryMeta[lastCat] ? lastCat : "alphabet";
    const curRows = rows.filter((r) => r.category === cur && r.completed).length;
    const pct = Math.min(100, Math.round((curRows / (categoryMeta[cur]?.total || 20)) * 100));
    return { current: cur, percent: pct };
  }, [rows, last]);

  const earnedBadges = allBadges.filter((b) => {
    const val = b.metric === "stars" ? totalStars : b.metric === "completed" ? completedCount : streak;
    return val >= b.threshold;
  });

  const nextBadge = allBadges
    .filter((b) => !earnedBadges.find((e) => e.id === b.id))
    .map((b) => {
      const val = b.metric === "stars" ? totalStars : b.metric === "completed" ? completedCount : streak;
      return { ...b, current: val, remaining: b.threshold - val };
    })
    .sort((a, b) => a.remaining - b.remaining)[0];

  if (loading) return null;
  if (!user && !last) return null; // brand-new visitors don't see this; marketing first

  const cur = categoryMeta[current];

  return (
    <section className="container mx-auto px-4 py-4" aria-label="Welcome back">
      <div className="rounded-3xl border-2 border-cyan-200 bg-gradient-to-br from-white via-cyan-50 to-amber-50 shadow-sm p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          {/* Greeting + path */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="text-3xl md:text-4xl shrink-0">{cur.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-fredoka font-extrabold text-cyan-900 truncate">
                  {user ? "Welcome back!" : "Pick up where you left off"}
                </h2>
                <span className="text-[11px] font-bold text-cyan-700/80 hidden sm:inline">
                  • {cur.label}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <Progress value={percent} className="h-2 bg-cyan-100" />
                <span className="text-xs font-bold text-cyan-800 w-10 text-right">{percent}%</span>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">
                {completedCount} lessons completed
                {percent < 100 ? ` • ${100 - percent}% to finish ${cur.label}` : " • Path complete 🎉"}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-800 px-2.5 py-1.5 rounded-full text-xs font-bold">
              <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {totalStars}
            </span>
            <span className="flex items-center gap-1 bg-rose-100 border border-rose-300 text-rose-700 px-2.5 py-1.5 rounded-full text-xs font-bold">
              <Flame className="w-3.5 h-3.5" /> {streak}d
            </span>
            <span className="flex items-center gap-1 bg-violet-100 border border-violet-300 text-violet-700 px-2.5 py-1.5 rounded-full text-xs font-bold">
              <Award className="w-3.5 h-3.5" /> {earnedBadges.length}
            </span>
          </div>

          {/* CTA */}
          <Link
            to={cur.path}
            className="inline-flex items-center justify-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm px-4 py-2 rounded-full shadow shrink-0"
          >
            <Play className="w-4 h-4" /> Continue Learning
          </Link>
        </div>

        {/* Earned badges + next milestone (only when relevant) */}
        {(earnedBadges.length > 0 || nextBadge) && (
          <div className="mt-3 pt-3 border-t border-cyan-100 flex flex-wrap items-center gap-2">
            {earnedBadges.length > 0 && (
              <>
                <span className="text-[11px] font-bold uppercase tracking-wide text-cyan-700">
                  Earned
                </span>
                {earnedBadges.map((b) => (
                  <span
                    key={b.id}
                    title={b.label}
                    className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-800 px-2 py-1 rounded-full text-xs font-bold"
                  >
                    <span className="text-sm">{b.icon}</span> {b.label}
                  </span>
                ))}
              </>
            )}
            {nextBadge && (
              <span className="inline-flex items-center gap-1 bg-white border border-dashed border-violet-300 text-violet-700 px-2 py-1 rounded-full text-xs font-semibold ml-auto">
                <Sparkles className="w-3 h-3" />
                Next: {nextBadge.icon} {nextBadge.label} — {nextBadge.remaining} more{" "}
                {nextBadge.metric === "stars" ? "⭐" : nextBadge.metric === "completed" ? "lessons" : "days"}
                <ArrowRight className="w-3 h-3" />
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default LearningJourney;
